/**
 * アイコン。
 *
 * 絵文字は OS とフォントで字形が変わり、同じ画面が環境ごとに違って見える
 * (2 色になったり、そもそも表示できなかったりする)。
 * そのため表示は lucide-react の SVG に統一する。
 *
 * ## 名前で受け取る
 *
 * 呼び出し側はコンポーネントではなく名前 (`"utensils"` など) を渡す。
 * こうすると `@spli/core` が返す `ExpenseIconName` をそのまま渡せて、
 * ドメイン層に React への依存を持ち込まずに済む。
 *
 * ## 色と大きさ
 *
 * currentColor で描くので、色は CSS 側 (親の color) で決める。
 * 大きさは size で渡すが、既定はテキストと並べたときに沈まない 20px。
 */
import {
  Ban,
  Beer,
  Check,
  CircleCheck,
  Copy,
  FerrisWheel,
  FolderOpen,
  Gift,
  Hand,
  Handshake,
  Hotel,
  Hourglass,
  Inbox,
  Landmark,
  Lightbulb,
  PartyPopper,
  QrCode,
  Receipt,
  Search,
  Settings,
  Share2,
  ShoppingBag,
  Trash2,
  TrainFront,
  TriangleAlert,
  Users,
  Utensils,
  X,
  Zap,
  type LucideIcon,
} from "lucide-react";
import type { ExpenseIconName } from "@spli/core";

/**
 * 支出カテゴリ以外に、画面で使うアイコンの名前。
 * ExpenseIconName と合わせて Icon の name に渡せる。
 */
export type UiIconName =
  | "settings"
  | "qr-code"
  | "check"
  | "check-circle"
  | "loading"
  | "empty-folder"
  | "search"
  | "members"
  | "raise-hand"
  | "bank"
  | "celebrate"
  | "handshake"
  | "warning"
  | "tip"
  | "mail-sent"
  | "drink"
  | "no-entry"
  | "instant"
  | "trash"
  | "close"
  | "copy"
  | "share";

export type IconName = ExpenseIconName | UiIconName;

const ICONS: Record<IconName, LucideIcon> = {
  // 支出カテゴリ (@spli/core の EXPENSE_CATEGORY_ICON と対応)
  utensils: Utensils,
  train: TrainFront,
  hotel: Hotel,
  "ferris-wheel": FerrisWheel,
  "shopping-bag": ShoppingBag,
  lightbulb: Lightbulb,
  gift: Gift,
  receipt: Receipt,

  // 画面のアイコン
  settings: Settings,
  "qr-code": QrCode,
  check: Check,
  "check-circle": CircleCheck,
  loading: Hourglass,
  "empty-folder": FolderOpen,
  search: Search,
  members: Users,
  "raise-hand": Hand,
  bank: Landmark,
  celebrate: PartyPopper,
  handshake: Handshake,
  warning: TriangleAlert,
  tip: Lightbulb,
  "mail-sent": Inbox,
  drink: Beer,
  "no-entry": Ban,
  instant: Zap,
  trash: Trash2,
  close: X,
  copy: Copy,
  share: Share2,
};

export interface IconProps {
  name: IconName;
  /** ピクセル。既定はテキストと並べる大きさ */
  size?: number;
  className?: string;
  /**
   * 読み上げ用の説明。
   * 省略時は装飾扱い (aria-hidden) にする。文字と一緒に置く場合は省略でよい。
   */
  label?: string;
}

export function Icon({ name, size = 20, className, label }: IconProps) {
  const Component = ICONS[name];

  return (
    <Component
      className={className}
      size={size}
      strokeWidth={1.75}
      aria-hidden={label === undefined}
      aria-label={label}
      role={label === undefined ? undefined : "img"}
      focusable="false"
    />
  );
}
